import {
  Badge, Box, Button, Container, Flex, Heading, HStack, Icon,
  SimpleGrid, Stack, Text, VStack,
} from "@chakra-ui/react";
import { ArrowForwardIcon, CheckCircleIcon } from "@chakra-ui/icons";
import {
  FaBrain, FaChartLine, FaCode, FaDatabase, FaGlobe, FaLayerGroup,
  FaNetworkWired, FaRobot, FaShieldAlt, FaUsers,
} from "react-icons/fa";
import { Link as RouterLink } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./utilities/navbar";
import Footer from "./utilities/footer";
import { SERVICES } from "./utilities/config";
import { theme } from "./utilities/theme";

const MotionBox = motion.create(Box);

const customerGoals = [
  { icon: FaLayerGroup, title: "Launch a digital product", text: "Turn a concept into a usable website, web platform, mobile app, or desktop tool.", link: "/services", linkLabel: "Compare build options", color: "#FF6F20" },
  { icon: FaRobot, title: "Automate a workflow", text: "Connect AI, business rules, and human review to reduce repetitive operational work.", link: "/aiagents", linkLabel: "Explore AI agents", color: "#00FFD0" },
  { icon: FaNetworkWired, title: "Connect systems and data", text: "Create dependable APIs, integrations, data pipelines, and scheduled collection flows.", link: "/apigateways", linkLabel: "See integration services", color: "#38BDF8" },
  { icon: FaBrain, title: "Make the right technical plan", text: "Clarify scope, architecture, priorities, and risk before committing a full build budget.", link: "/consultations", linkLabel: "Plan with an expert", color: "#A78BFA" },
];

const featuredServiceNames = ["AI Agents", "Web Applications", "CRM Platforms", "Mobile Apps", "API Gateways", "Web Scrapers"];
const serviceContext = {
  "AI Agents": { result: "Guided automation", fit: "Support, research, and operations" },
  "Web Applications": { result: "A complete business tool", fit: "SaaS, portals, and dashboards" },
  "CRM Platforms": { result: "One customer workflow", fit: "Sales and service teams" },
  "Mobile Apps": { result: "A release-ready app", fit: "Field teams and customer products" },
  "API Gateways": { result: "Connected, secure systems", fit: "Apps, vendors, and internal services" },
  "Web Scrapers": { result: "Structured, repeatable data", fit: "Research and data operations" },
};

const deliverySteps = [
  { number: "01", title: "Discover the real need", text: "We map users, business outcomes, current tools, data, constraints, and the riskiest assumptions." },
  { number: "02", title: "Shape the right first release", text: "You get a clear scope and solution path focused on the smallest version that creates real value." },
  { number: "03", title: "Build in visible milestones", text: "Design, engineering, integrations, and testing move in reviewable slices instead of a black box." },
  { number: "04", title: "Launch, learn, and improve", text: "We prepare the system for handoff, deployment, measurement, and the next practical iteration." },
];

const platformLayers = [
  { label: "Customer experience", detail: "Web, mobile, portals", icon: FaGlobe, color: "#FF6F20" },
  { label: "Workflow intelligence", detail: "AI agents and automation", icon: FaBrain, color: "#00FFD0" },
  { label: "Business systems", detail: "CRM, APIs, integrations", icon: FaNetworkWired, color: "#38BDF8" },
  { label: "Reliable foundation", detail: "Data, security, cloud", icon: FaShieldAlt, color: "#A78BFA" },
];

export default function Home() {
  const { homeMainBg: mainBg, homeCardBg: cardBg, accentColor, accentCyan, textColor, textSecondary } = theme.colors;
  const featuredServices = SERVICES.filter((service) => featuredServiceNames.includes(service.name));

  return (
    <Box bg={mainBg} color={textColor} minH="100vh">
      <Navbar />
      <Box as="main" id="main-content" position="relative" overflow="hidden">
        <Box
          as="section"
          position="relative"
          borderBottom="1px solid rgba(255,255,255,0.08)"
          _before={{ content: '""', position: "absolute", top: "-180px", right: "-120px", w: "620px", h: "620px", borderRadius: "full", bg: "radial-gradient(circle, rgba(0,255,208,0.14), transparent 68%)", pointerEvents: "none" }}
          _after={{ content: '""', position: "absolute", bottom: "-260px", left: "-180px", w: "560px", h: "560px", borderRadius: "full", bg: "radial-gradient(circle, rgba(255,111,32,0.12), transparent 68%)", pointerEvents: "none" }}
        >
          <Container maxW="1360px" px={{ base: 4, md: 8 }} py={{ base: 14, md: 20, xl: 24 }} position="relative" zIndex={1}>
            <Stack direction={{ base: "column", lg: "row" }} spacing={{ base: 12, lg: 14 }} align="center">
              <MotionBox flex="1.08" initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.55 }}>
                <Badge bg="rgba(0,255,208,0.1)" color={accentCyan} border="1px solid rgba(0,255,208,0.28)" borderRadius="full" px={4} py={1.5} mb={6} letterSpacing="wide">
                  Product engineering + intelligent automation
                </Badge>
                <Heading as="h1" fontSize={{ base: "4xl", md: "6xl", xl: "7xl" }} lineHeight="1.02" letterSpacing="-0.045em" maxW="780px" mb={6}>
                  From complex ideas to{" "}<Box as="span" color={accentCyan}>useful software.</Box>
                </Heading>
                <Text fontSize={{ base: "md", md: "xl" }} color={textSecondary} lineHeight="1.75" maxW="720px">
                  Orvitrix designs and builds web platforms, mobile apps, CRM systems,
                  APIs, data workflows, and AI agents—connected around the way your
                  business actually works.
                </Text>
                <HStack spacing={3} flexWrap="wrap" mt={8}>
                  <Button as={RouterLink} to="/contact" bg={accentColor} color="white" rightIcon={<ArrowForwardIcon />} borderRadius="lg" size="lg" px={7} _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}>
                    Discuss your project
                  </Button>
                  <Button as={RouterLink} to="/services" variant="outline" borderColor="rgba(0,255,208,0.5)" color={accentCyan} borderRadius="lg" size="lg" px={7} _hover={{ bg: "rgba(0,255,208,0.08)" }}>
                    Explore what we build
                  </Button>
                </HStack>
                <HStack spacing={{ base: 3, md: 5 }} flexWrap="wrap" mt={7}>
                  {["Clear scope before engineering begins", "Human review for consequential automation", "Security built into delivery"].map((signal) => (
                    <HStack key={signal} spacing={2} align="start">
                      <CheckCircleIcon color={accentCyan} mt="3px" />
                      <Text color={textSecondary} fontSize="sm">{signal}</Text>
                    </HStack>
                  ))}
                </HStack>
              </MotionBox>
              <ProductSystemPreview cardBg={cardBg} />
            </Stack>
          </Container>
        </Box>

        <Section>
          <SectionHeading eyebrow="Start With Your Goal" title="Not sure which service you need? Start here." text="Choose the outcome closest to your current challenge. Each path explains the typical solution, what is included, and how delivery works." />
          <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing={5}>
            {customerGoals.map((goal) => <GoalCard key={goal.title} goal={goal} />)}
          </SimpleGrid>
        </Section>

        <Section bg="rgba(15,23,42,0.72)">
          <Flex align={{ base: "start", md: "end" }} justify="space-between" direction={{ base: "column", md: "row" }} gap={5} mb={8}>
            <SectionHeading eyebrow="Solutions" title="One team across product, automation, and infrastructure" text="Choose a focused engagement or combine capabilities into one complete product system." mb={0} />
            <Button as={RouterLink} to="/services" variant="ghost" color={accentCyan} rightIcon={<ArrowForwardIcon />} flexShrink={0} _hover={{ bg: "rgba(0,255,208,0.08)" }}>View all services</Button>
          </Flex>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={5}>
            {featuredServices.map((service) => <ServiceCard key={service.name} service={service} context={serviceContext[service.name]} cardBg={cardBg} />)}
          </SimpleGrid>
        </Section>

        <Section>
          <Stack direction={{ base: "column", lg: "row" }} spacing={{ base: 10, lg: 16 }} align="start">
            <Box flex="0.78" position={{ lg: "sticky" }} top={{ lg: "110px" }}>
              <SectionHeading eyebrow="How Delivery Works" title="Clarity at every stage—not a black-box build" text="You always know what is being decided, what is being built, and what comes next." />
              <Button as={RouterLink} to="/about" variant="outline" borderColor="rgba(255,255,255,0.18)" color={textColor} rightIcon={<ArrowForwardIcon />} _hover={{ bg: "rgba(255,255,255,0.06)", borderColor: accentCyan }}>Learn about Orvitrix</Button>
            </Box>
            <VStack align="stretch" spacing={4} flex="1.22" w="100%">
              {deliverySteps.map((step) => (
                <Flex key={step.number} gap={{ base: 4, md: 6 }} align="start" bg={cardBg} border="1px solid rgba(255,255,255,0.1)" borderRadius="xl" p={{ base: 5, md: 6 }}>
                  <Text color={accentColor} fontSize={{ base: "xl", md: "2xl" }} lineHeight="1" fontWeight="black" letterSpacing="-0.04em" minW="38px">{step.number}</Text>
                  <Box><Heading size="md" mb={2}>{step.title}</Heading><Text color={textSecondary} lineHeight="1.7">{step.text}</Text></Box>
                </Flex>
              ))}
            </VStack>
          </Stack>
        </Section>

        <Section bg="rgba(9,16,29,0.94)">
          <Stack direction={{ base: "column", lg: "row" }} spacing={8} align="stretch">
            <Box flex="1" borderRadius="2xl" p={{ base: 6, md: 9 }} bg="linear-gradient(145deg, rgba(0,255,208,0.1), rgba(56,189,248,0.03))" border="1px solid rgba(0,255,208,0.2)">
              <Badge color={accentCyan} bg="rgba(0,255,208,0.1)" borderRadius="full" mb={5}>Connected by design</Badge>
              <Heading size={{ base: "lg", md: "xl" }} mb={4}>Build a product, not a pile of disconnected features.</Heading>
              <Text color={textSecondary} lineHeight="1.8" mb={7}>Interfaces, automation, data, integrations, permissions, and deployment should work as parts of the same system.</Text>
              <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={3}>
                {platformLayers.map((layer) => (
                  <HStack key={layer.label} spacing={3} bg="rgba(6,12,24,0.52)" border="1px solid rgba(255,255,255,0.09)" borderRadius="lg" p={4}>
                    <Icon as={layer.icon} color={layer.color} boxSize={5} />
                    <Box><Text fontWeight="bold" fontSize="sm">{layer.label}</Text><Text color={textSecondary} fontSize="xs">{layer.detail}</Text></Box>
                  </HStack>
                ))}
              </SimpleGrid>
            </Box>
            <Box flex="0.72" p={{ base: 6, md: 9 }} bg={cardBg} border="1px solid rgba(255,255,255,0.1)" borderRadius="2xl">
              <Icon as={FaChartLine} color={accentColor} boxSize={8} mb={5} />
              <Heading size="lg" mb={4}>Bring the business problem first.</Heading>
              <Text color={textSecondary} lineHeight="1.8" mb={7}>You do not need a complete specification. Tell us what is slow, confusing, disconnected, or ready to become a product. We will help identify the most practical next move.</Text>
              <Button as={RouterLink} to="/contact" bg={accentColor} color="white" rightIcon={<ArrowForwardIcon />} borderRadius="lg" size="lg" _hover={{ bg: "#ff853d", transform: "translateY(-2px)" }}>Start the conversation</Button>
            </Box>
          </Stack>
        </Section>
      </Box>
      <Footer />
    </Box>
  );
}

function Section({ children, bg }) {
  return <Box as="section" bg={bg} py={{ base: 12, md: 18 }}><Container maxW="1360px" px={{ base: 4, md: 8 }}>{children}</Container></Box>;
}

function ProductSystemPreview({ cardBg }) {
  const { textSecondary, accentCyan } = theme.colors;
  return (
    <MotionBox flex="0.92" w="100%" maxW={{ base: "680px", lg: "570px" }} initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.65, delay: 0.1 }} bg="rgba(6,12,24,0.82)" border="1px solid rgba(0,255,208,0.2)" borderRadius="2xl" boxShadow="0 30px 90px rgba(0,0,0,0.38)" overflow="hidden">
      <Flex align="center" justify="space-between" px={5} py={4} borderBottom="1px solid rgba(255,255,255,0.08)">
        <HStack spacing={2}>{["#FF6F20", "#FFB400", "#00FFD0"].map((color) => <Box key={color} w={2.5} h={2.5} bg={color} borderRadius="full" />)}</HStack>
        <Text color={textSecondary} fontSize="xs" fontWeight="bold" letterSpacing="wide">YOUR PRODUCT SYSTEM</Text>
      </Flex>
      <Box p={{ base: 5, md: 6 }}>
        <Flex align="center" justify="space-between" mb={5}><Box><Text color={textSecondary} fontSize="xs">Business workflow</Text><Heading size="sm">From first touch to outcome</Heading></Box><Badge color={accentCyan} bg="rgba(0,255,208,0.1)" borderRadius="full">Connected</Badge></Flex>
        <SimpleGrid columns={2} spacing={3} mb={3}>
          <PreviewTile icon={FaGlobe} label="Customer app" value="Clear experience" color="#FF6F20" />
          <PreviewTile icon={FaRobot} label="AI workflow" value="Guided automation" color="#00FFD0" />
          <PreviewTile icon={FaUsers} label="Business system" value="Shared context" color="#A78BFA" />
          <PreviewTile icon={FaDatabase} label="Data layer" value="Reliable records" color="#38BDF8" />
        </SimpleGrid>
        <Box bg={cardBg} border="1px solid rgba(255,255,255,0.09)" borderRadius="xl" p={4}>
          <Flex align="center" justify="space-between" mb={3}><HStack><Icon as={FaCode} color={accentCyan} /><Text fontSize="sm" fontWeight="bold">Delivery visibility</Text></HStack><Text color={accentCyan} fontSize="xs">Milestone 03</Text></Flex>
          <Box h={2} bg="rgba(255,255,255,0.08)" borderRadius="full" overflow="hidden"><Box h="100%" w="68%" bg="linear-gradient(90deg, #FF6F20, #00FFD0)" borderRadius="full" /></Box>
          <Flex justify="space-between" mt={2} color={textSecondary} fontSize="xs"><Text>Working software</Text><Text>Reviewable progress</Text></Flex>
        </Box>
      </Box>
    </MotionBox>
  );
}

function PreviewTile({ icon, label, value, color }) {
  const { textSecondary } = theme.colors;
  return <Box bg="rgba(15,23,42,0.68)" border="1px solid rgba(255,255,255,0.09)" borderRadius="xl" p={{ base: 3, md: 4 }}><Icon as={icon} color={color} boxSize={5} mb={3} /><Text color={textSecondary} fontSize="xs">{label}</Text><Text fontWeight="bold" fontSize={{ base: "xs", md: "sm" }}>{value}</Text></Box>;
}

function GoalCard({ goal }) {
  const { homeCardBg, textColor, textSecondary } = theme.colors;
  return (
    <Flex as={RouterLink} to={goal.link} direction="column" bg={homeCardBg} color={textColor} border="1px solid rgba(255,255,255,0.1)" borderRadius="xl" p={6} minH="100%" textDecoration="none" transition="all 0.22s ease" _hover={{ transform: "translateY(-5px)", borderColor: goal.color + "88", textDecoration: "none" }} _focusVisible={{ outline: "none", boxShadow: "0 0 0 3px " + goal.color + "66" }}>
      <Flex align="center" justify="center" w={12} h={12} borderRadius="lg" bg={goal.color + "16"} border="1px solid" borderColor={goal.color + "4D"} mb={5}><Icon as={goal.icon} color={goal.color} boxSize={6} /></Flex>
      <Heading size="md" mb={3}>{goal.title}</Heading><Text color={textSecondary} fontSize="sm" lineHeight="1.7" mb={6}>{goal.text}</Text>
      <HStack mt="auto" color={goal.color} fontWeight="bold" fontSize="sm"><Text>{goal.linkLabel}</Text><ArrowForwardIcon /></HStack>
    </Flex>
  );
}

function ServiceCard({ service, context, cardBg }) {
  const { textColor, textSecondary } = theme.colors;
  return (
    <Flex as={RouterLink} to={service.path} direction="column" bg={cardBg} color={textColor} border="1px solid rgba(255,255,255,0.1)" borderRadius="xl" p={{ base: 5, md: 6 }} textDecoration="none" transition="all 0.22s ease" _hover={{ transform: "translateY(-4px)", borderColor: service.color + "88", textDecoration: "none" }} _focusVisible={{ outline: "none", boxShadow: "0 0 0 3px " + service.color + "66" }}>
      <Flex align="center" justify="space-between" mb={5}><Flex align="center" justify="center" w={11} h={11} borderRadius="lg" bg={service.color + "16"} border="1px solid" borderColor={service.color + "4D"}><Icon as={service.icon} color={service.color} boxSize={5} /></Flex><ArrowForwardIcon color={service.color} /></Flex>
      <Heading size="md" mb={2}>{service.name}</Heading><Text color={textSecondary} fontSize="sm" lineHeight="1.7" mb={5}>{service.description}</Text>
      <SimpleGrid columns={2} spacing={3} mt="auto" pt={4} borderTop="1px solid rgba(255,255,255,0.08)"><Box><Text color={textSecondary} fontSize="xs">Typical result</Text><Text fontSize="xs" fontWeight="bold">{context.result}</Text></Box><Box><Text color={textSecondary} fontSize="xs">Best for</Text><Text fontSize="xs" fontWeight="bold">{context.fit}</Text></Box></SimpleGrid>
    </Flex>
  );
}

function SectionHeading({ eyebrow, title, text, mb = 8 }) {
  const { accentCyan, textSecondary } = theme.colors;
  return <Box mb={mb}><Text color={accentCyan} fontSize="sm" fontWeight="bold" textTransform="uppercase" letterSpacing="0.12em" mb={2}>{eyebrow}</Text><Heading size={{ base: "lg", md: "xl" }} letterSpacing="-0.025em" mb={3}>{title}</Heading><Text color={textSecondary} maxW="760px" lineHeight="1.8">{text}</Text></Box>;
}
